import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NCType, NCTypeSearchParams } from '../../services/nctypeApi';

// State interfaces
export interface NCTypeState {
  // Data
  items: NCType[];
  selectedItem: NCType | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  
  // Form State
  formData: {
    Name: string;
    Description: string;
    Active: boolean;
  };
  
  // Validation
  validationErrors: string[];
  
  // Search and Filter
  searchParams: NCTypeSearchParams;
  
  // Pagination
  pagination: {
    page: number;
    pageSize: number;
    total: number;
  };
  
  // Statistics
  statistics: {
    total: number;
    active: number;
    inactive: number;
  };
  
  // Bulk Operations
  selectedIds: number[];
  bulkOperationLoading: boolean;
  
  // Success Messages
  successMessage: string;
}

const initialState: NCTypeState = {
  // Data
  items: [],
  selectedItem: null,
  
  // UI State
  loading: false,
  error: null,
  
  // Form State
  formData: {
    Name: '',
    Description: '',
    Active: true,
  },
  
  // Validation
  validationErrors: [],
  
  // Search and Filter
  searchParams: {
    page: 1,
    pageSize: 10,
    search: '',
    active: undefined,
    sortBy: 'Name',
    sortOrder: 'asc',
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
  },
  
  // Statistics
  statistics: {
    total: 0,
    active: 0,
    inactive: 0,
  },
  
  // Bulk Operations
  selectedIds: [],
  bulkOperationLoading: false,
  
  // Success Messages
  successMessage: '',
};

const nctypeSlice = createSlice({
  name: 'nctype',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    // Data management
    setItems: (state, action: PayloadAction<NCType[]>) => {
      state.items = action.payload;
    },
    setSelectedItem: (state, action: PayloadAction<NCType | null>) => {
      state.selectedItem = action.payload;
    },
    addItem: (state, action: PayloadAction<NCType>) => {
      state.items.unshift(action.payload);
    },
    updateItem: (state, action: PayloadAction<NCType>) => {
      const index = state.items.findIndex(item => item.Id === action.payload.Id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    removeItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.Id !== action.payload);
    },
    
    // Form management
    setFormData: (state, action: PayloadAction<Partial<NCTypeState['formData']>>) => {
      state.formData = { ...state.formData, ...action.payload };
      // Clear validation errors when form data changes
      if (state.validationErrors.length > 0) {
        state.validationErrors = [];
      }
    },
    resetFormData: (state) => {
      state.formData = initialState.formData;
      state.validationErrors = [];
    },
    
    // Validation
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    
    // Search and filter
    setSearchParams: (state, action: PayloadAction<Partial<NCTypeSearchParams>>) => {
      state.searchParams = { ...state.searchParams, ...action.payload };
    },
    resetSearchParams: (state) => {
      state.searchParams = initialState.searchParams;
    },
    
    // Pagination
    setPagination: (state, action: PayloadAction<Partial<NCTypeState['pagination']>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Statistics
    setStatistics: (state, action: PayloadAction<NCTypeState['statistics']>) => {
      state.statistics = action.payload;
    },
    
    // Bulk operations
    setSelectedIds: (state, action: PayloadAction<number[]>) => {
      state.selectedIds = action.payload;
    },
    addSelectedId: (state, action: PayloadAction<number>) => {
      if (!state.selectedIds.includes(action.payload)) {
        state.selectedIds.push(action.payload);
      }
    },
    removeSelectedId: (state, action: PayloadAction<number>) => {
      state.selectedIds = state.selectedIds.filter(id => id !== action.payload);
    },
    clearSelectedIds: (state) => {
      state.selectedIds = [];
    },
    setBulkOperationLoading: (state, action: PayloadAction<boolean>) => {
      state.bulkOperationLoading = action.payload;
    },
    
    // Success messages
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = '';
    },
    
    // Reset state
    resetNCTypeState: () => initialState,
  },
});

// Action creators
export const {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setFormData,
  resetFormData,
  setValidationErrors,
  clearValidationErrors,
  setSearchParams,
  resetSearchParams,
  setPagination,
  setStatistics,
  setSelectedIds,
  addSelectedId,
  removeSelectedId,
  clearSelectedIds,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  resetNCTypeState,
} = nctypeSlice.actions;

// Saga action types
export const FETCH_NCTYPES = 'nctype/fetchNCTypes';
export const FETCH_NCTYPE_BY_ID = 'nctype/fetchNCTypeById';
export const CREATE_NCTYPE = 'nctype/createNCType';
export const UPDATE_NCTYPE = 'nctype/updateNCType';
export const DELETE_NCTYPE = 'nctype/deleteNCType';
export const SEARCH_NCTYPES = 'nctype/searchNCTypes';
export const FETCH_STATISTICS = 'nctype/fetchStatistics';
export const BULK_ACTIVATE = 'nctype/bulkActivate';
export const BULK_DEACTIVATE = 'nctype/bulkDeactivate';
export const BULK_DELETE = 'nctype/bulkDelete';
export const EXPORT_TO_CSV = 'nctype/exportToCSV';

// Saga action creators
export const fetchNCTypes = (params?: NCTypeSearchParams) => ({ type: FETCH_NCTYPES, payload: params });
export const fetchNCTypeById = (id: number) => ({ type: FETCH_NCTYPE_BY_ID, payload: id });
export const createNCType = (nctype: Omit<NCType, 'Id'>) => ({ type: CREATE_NCTYPE, payload: nctype });
export const updateNCType = (id: number, nctype: Partial<NCType>) => ({ type: UPDATE_NCTYPE, payload: { id, nctype } });
export const deleteNCType = (id: number) => ({ type: DELETE_NCTYPE, payload: id });
export const searchNCTypes = (params: NCTypeSearchParams) => ({ type: SEARCH_NCTYPES, payload: params });
export const fetchStatistics = () => ({ type: FETCH_STATISTICS });
export const bulkActivate = (ids: number[]) => ({ type: BULK_ACTIVATE, payload: ids });
export const bulkDeactivate = (ids: number[]) => ({ type: BULK_DEACTIVATE, payload: ids });
export const bulkDelete = (ids: number[]) => ({ type: BULK_DELETE, payload: ids });
export const exportToCSV = (params?: NCTypeSearchParams) => ({ type: EXPORT_TO_CSV, payload: params });

export default nctypeSlice.reducer; 